import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  X, Clock, Megaphone, PhoneCall, CalendarCheck, Wallet, FileText,
  Users, LifeBuoy, Rocket, Calendar, ClipboardList, Bell,
} from "lucide-react";
import { useAlerts } from "@/context/AlertsContext";
import { ALERT_CATEGORIES, ALERT_PRIORITIES, SNOOZE_OPTIONS, RELATED_MODULES } from "@/lib/alertsData";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const ICONS = { Megaphone, PhoneCall, CalendarCheck, Wallet, FileText, Users, LifeBuoy, Rocket, Calendar, ClipboardList };

function AlertPopup({ item }) {
  const { dismiss, snooze, open } = useAlerts();
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShown(true), 20); return () => clearTimeout(t); }, []);

  const rule = item.rule;
  const cat = ALERT_CATEGORIES[rule.category] || ALERT_CATEGORIES.general;
  const prio = ALERT_PRIORITIES[rule.priority] || ALERT_PRIORITIES.medium;
  const Icon = ICONS[cat.icon] || Bell;
  const mod = RELATED_MODULES[rule.related_module];
  const hasModule = mod && mod.path;
  const iconBg = { error: "bg-red-500/10", warning: "bg-amber-500/10", info: "bg-sky-500/10", neutral: "bg-muted" }[prio.tone] || "bg-muted";

  return (
    <div
      className={`pointer-events-auto flex w-[320px] max-w-[calc(100vw-2rem)] items-center gap-2.5 rounded-full border border-border/70 bg-popover py-2 pl-2.5 pr-2 shadow-xl transition-all duration-300 ease-out ${hasModule ? "cursor-pointer" : ""}`}
      style={{ transform: shown ? "translateX(0)" : "translateX(24px)", opacity: shown ? 1 : 0 }}
      onClick={() => hasModule && open(item)}
      data-testid={`alert-popup-${rule.id}`}
    >
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconBg}`}>
        <Icon className="h-4 w-4 text-foreground/80" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold leading-tight text-foreground" data-testid="alert-title">{rule.alert_title}</div>
        <div className="truncate text-[11px] text-muted-foreground">{cat.label} · {prio.label}</div>
      </div>
      <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" title="Posticipa" data-testid="alert-snooze"><Clock className="h-3.5 w-3.5" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-[11px]">Posticipa di…</DropdownMenuLabel>
            {Object.entries(SNOOZE_OPTIONS).map(([k, v]) => (
              <DropdownMenuItem key={k} onClick={() => snooze(item, k)} data-testid={`alert-snooze-${k}`}>{v.label}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button onClick={() => dismiss(item)} className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" data-testid="alert-dismiss" aria-label="Chiudi"><X className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function AlertStack() {
  const ctx = useAlerts();
  const location = useLocation();
  if (!ctx || !ctx.queue.length) return null;
  return (
    <div className="pointer-events-none fixed bottom-24 right-6 z-[120] flex flex-col gap-3" data-testid="alert-stack">
      {ctx.queue.map((item) => <AlertPopup key={item.qid} item={item} />)}
    </div>
  );
}
